/**
 * `/teilnehmer` — die Teilnehmer-Übersicht.
 *
 * Die zweite Admin-Oberfläche aus Bernds Beschreibung: sie führt zusammen, ob das Programm
 * angenommen wird und ob die Teilnehmer finanziell mitkommen. Sie liegt im Foundation-Portal
 * hinter Entra und nicht in einer Mini-App, weil sie Salden liest — eine App, die jemand per
 * Einladungslink bekommt, darf das nie.
 *
 * 🔴 Jede Abfrage auf dieser Seite schreibt einen Protokolleintrag. Deshalb lädt nichts von
 * selbst, was niemand sehen wollte: kein Vorausladen, und solange das Tor zu ist, läuft keine
 * einzige Abfrage (`enabled` in `api.ts`).
 */
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'
import { z } from 'zod'

import { Kohorten } from '~/components/teilnehmer/kohorten.tsx'
import { TeilnehmerListe } from '~/components/teilnehmer/teilnehmer-liste.tsx'
import { Verpflichtung } from '~/components/teilnehmer/verpflichtung.tsx'
import {
  listeQuery,
  uebersichtQuery,
  zugangQuery,
  type ListeInput,
} from '~/components/teilnehmer/api.ts'
import { TechnicalErrorAlert } from '~/components/technical-error-alert.tsx'
import { Card, CardContent } from '~/components/ui/card.tsx'
import { Pagination } from '~/components/ui/pagination.tsx'
import { useDebounced } from '~/lib/use-debounced.ts'
import { cn } from '~/lib/utils.ts'

const searchSchema = z.object({
  band: z
    .enum(['unter_13', '13_15', '16_17', '18_20', '21_26', 'ueber_26', 'unbekannt'])
    .optional(),
  q: z.string().trim().max(100).optional(),
  nurRueckstand: z.boolean().optional(),
  nurAbbruch: z.boolean().optional(),
  nurMitTeilnahme: z.boolean().optional(),
  sortieren: z.enum(['name', 'quote', 'saldo', 'zuletzt']).default('name'),
  seite: z.number().int().min(1).default(1),
})

const SEITENGROESSE = 50

export const Route = createFileRoute('/teilnehmer/')({
  validateSearch: searchSchema,
  component: TeilnehmerSeite,
})

function TeilnehmerSeite() {
  const suche = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const zugang = useQuery(zugangQuery())
  const darfSehen = zugang.data?.darfSehen === true

  const uebersicht = useQuery(uebersichtQuery(darfSehen))

  /**
   * Der Suchbegriff wird entprellt, und hier ist das kein UX-Detail: jede Abfrage schreibt einen
   * Protokolleintrag. Ohne Entprellung stünden im Protokoll acht Zeilen für ein getipptes Wort,
   * und das Protokoll wäre nicht mehr lesbar — genau das, wofür es da ist, wäre kaputt.
   */
  const q = useDebounced(suche.q ?? '', 350)

  const eingabe = useMemo<ListeInput>(
    () => ({
      band: suche.band,
      q: q.trim() === '' ? undefined : q.trim(),
      nurRueckstand: suche.nurRueckstand,
      nurAbbruch: suche.nurAbbruch,
      nurMitTeilnahme: suche.nurMitTeilnahme,
      sortieren: suche.sortieren,
      limit: SEITENGROESSE,
      offset: (suche.seite - 1) * SEITENGROESSE,
    }),
    [suche, q],
  )

  const liste = useQuery(listeQuery(eingabe, darfSehen))

  function setzen(teil: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: (alt) => ({ ...alt, seite: 1, ...teil }) })
  }

  if (zugang.isPending) {
    return <p className="text-sm text-muted-foreground">Zugang wird geprüft …</p>
  }
  if (zugang.isError) return <TechnicalErrorAlert error={zugang.error} />
  if (!zugang.data || !darfSehen) {
    return (
      <div className="space-y-6">
        <Kopf />
        <Verpflichtung zugang={zugang.data} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Kopf />

      {uebersicht.isError ? <TechnicalErrorAlert error={uebersicht.error} /> : null}
      {uebersicht.data ? (
        <Kohorten
          baender={uebersicht.data.baender}
          gewaehlt={suche.band}
          fensterTage={uebersicht.data.fensterTage}
          onWaehlen={(band) => setzen({ band })}
        />
      ) : null}

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              type="search"
              inputMode="search"
              placeholder="Name oder Mitgliedsnummer"
              defaultValue={suche.q ?? ''}
              onChange={(e) => setzen({ q: e.target.value || undefined })}
              className="h-11 w-full rounded-md border bg-background px-3 text-sm sm:max-w-xs"
            />
            <select
              value={suche.sortieren}
              onChange={(e) =>
                setzen({ sortieren: e.target.value as z.infer<typeof searchSchema>['sortieren'] })
              }
              className="h-11 rounded-md border bg-background px-3 text-sm"
            >
              <option value="name">nach Name</option>
              <option value="quote">nach Teilnahme (schwächste zuerst)</option>
              <option value="saldo">nach Saldo (Rückstand zuerst)</option>
              <option value="zuletzt">nach „zuletzt da" (längste zuerst)</option>
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            <Schalter
              an={suche.nurAbbruch === true}
              onKlick={() => setzen({ nurAbbruch: suche.nurAbbruch ? undefined : true })}
            >
              kommt nicht mehr
            </Schalter>
            <Schalter
              an={suche.nurRueckstand === true}
              onKlick={() => setzen({ nurRueckstand: suche.nurRueckstand ? undefined : true })}
            >
              im Rückstand
            </Schalter>
            <Schalter
              an={suche.nurMitTeilnahme === true}
              onKlick={() => setzen({ nurMitTeilnahme: suche.nurMitTeilnahme ? undefined : true })}
            >
              nimmt teil
            </Schalter>
          </div>

          {liste.isError ? <TechnicalErrorAlert error={liste.error} /> : null}
          {liste.isPending ? (
            <p className="text-sm text-muted-foreground">wird geladen …</p>
          ) : liste.data ? (
            <>
              <p className="text-xs text-muted-foreground">
                {liste.data.total} Personen · Teilnahme über {liste.data.fensterTage} Tage
              </p>
              <TeilnehmerListe items={liste.data.items} />
              <Pagination
                total={liste.data.total}
                limit={liste.data.limit}
                offset={liste.data.offset}
                onOffsetChange={(offset) =>
                  void navigate({
                    search: (alt) => ({ ...alt, seite: Math.floor(offset / SEITENGROESSE) + 1 }),
                  })
                }
              />
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}

function Kopf() {
  return (
    <div className="space-y-2">
      <h1 className="text-2xl font-semibold">Teilnehmer</h1>
      <p className="max-w-3xl text-sm text-muted-foreground">
        Wer nimmt regelmäßig teil, wer ist nicht mehr gekommen, und wer kommt finanziell nicht mit.
        Alle Zahlen sind aus gemeldeten Teilnahmen und Salden gerechnet — es wird hier nichts über
        einen Menschen gespeichert, was nicht schon vorher eine Tatsache war.
      </p>
    </div>
  )
}

function Schalter({
  an,
  onKlick,
  children,
}: {
  an: boolean
  onKlick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={an}
      onClick={onKlick}
      className={cn(
        'min-h-11 rounded-full border px-4 text-sm transition-colors hover:bg-accent',
        an && 'border-primary bg-accent font-medium',
      )}
    >
      {children}
    </button>
  )
}
